import { useState } from 'react'
import { AlertTriangle, PiggyBank, Plane } from 'lucide-react'
import { EmptyState, ErrorState, FilterBar, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import {
  useAirTicketEntitlements, useBasicPayBasis, useGpssaConfig, useGpssaReport, useProvisionMutations,
  useProvisions,
} from '../api'
import { PROVISION_TYPE_LABELS } from '../types'
import type { ProvisionType } from '../types'
import { Select } from '@/components/ui/select'

interface LegalEntityOption { id: string; name: string }

type Tab = 'provisions' | 'gpssa' | 'air-tickets'

/**
 * What payroll owes but has not paid: gratuity, leave salary and air tickets.
 *
 * <p>The basis is stated at the top rather than left implicit. A column of zero gratuity
 * provisions is otherwise unexplainable, and the explanation — nothing is marked as making
 * up the basic wage — is a setting somebody can go and change.
 */
export function ProvisionsPage() {
  useBreadcrumb()
  const now = new Date()
  const [tab, setTab] = useState<Tab>('provisions')
  const [provisionType, setProvisionType] = useState<ProvisionType>('GRATUITY')
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [entityId, setEntityId] = useState('')
  const [error, setError] = useState<string | null>(null)

  const basis = useBasicPayBasis()
  const provisions = useProvisions(provisionType, year, month)
  const gpssaConfig = useGpssaConfig()
  const gpssaReport = useGpssaReport(year, month)
  const entitlements = useAirTicketEntitlements()
  const entities = useLookup<LegalEntityOption>('legalEntities')
  const { accrue } = useProvisionMutations()

  return (
    <div className="space-y-4">
      <PageHeader
        title="Provisions and statutory"
        subtitle="What is owed and not yet paid — accrued monthly so Finance can reconcile it."
      />

      {basis.data && (
        <Card
          className={`p-4 text-sm ${basis.data.source === 'NOT_CONFIGURED'
            ? 'border-amber-300 bg-amber-50 text-amber-900'
            : 'border-slate-200 bg-slate-50 text-slate-700'}`}
        >
          <p className="flex items-start gap-2">
            {basis.data.source === 'NOT_CONFIGURED' && (
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            )}
            <span>
              <strong>The basic wage gratuity accrues on: </strong>
              {basis.data.elementCodes.length > 0
                ? basis.data.elementCodes.join(', ')
                : 'nothing yet'}
              . {basis.data.note}
            </span>
          </p>
          <p className="mt-2 text-xs">
            UAE Federal Decree-Law 33/2021 article 51 computes end-of-service on the basic wage,
            excluding allowances. Which elements make that up is a fact about your contracts, so
            it is configuration rather than something this system decides.
          </p>
        </Card>
      )}

      <FilterBar>
        <Input
          aria-label="Year"
          id="prov-year" type="number" className="w-28" value={year}
          onChange={(event) => setYear(Number(event.target.value))}
        />

        <Select
          aria-label="Month"
          id="prov-month"
          value={month}
          onChange={(event) => setMonth(Number(event.target.value))}
        >
          {Array.from({ length: 12 }, (_, index) => index + 1).map((value) => (
            <option key={value} value={value}>
              {new Date(2000, value - 1, 1).toLocaleString('en', { month: 'long' })}
            </option>
          ))}
        </Select>

                {tab === 'provisions' && (
                  <>
        <Select
          aria-label="Provision"
          id="prov-type"
          value={provisionType}
          onChange={(event) => setProvisionType(event.target.value as ProvisionType)}
        >
          {Object.entries(PROVISION_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </Select>

        <Select
          aria-label="Entity"
          id="prov-entity"
          value={entityId}
          onChange={(event) => setEntityId(event.target.value)}
        >
          <option value="">Choose an entity…</option>
          {entities.data?.map((entity) => (
            <option key={entity.id} value={entity.id}>{entity.name}</option>
          ))}
        </Select>

                    <Button
                      type="button"
                      disabled={!entityId || accrue.isPending}
                      onClick={() => {
                        setError(null)
                        void accrue.mutateAsync({ legalEntityId: entityId, year, month, provisionType })
                          .catch((caught: unknown) =>
                            setError(toApiProblem(caught)?.detail ?? 'That month could not be accrued'))
                      }}
                    >
                      Accrue this month
                    </Button>
                  </>
                )}
      </FilterBar>

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <div className="flex gap-2 border-b border-slate-200">
        {([
          ['provisions', 'Provision ledger'],
          ['gpssa', 'GPSSA'],
          ['air-tickets', 'Air tickets'],
        ] as const).map(([name, label]) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            className={`px-3 py-2 text-sm ${tab === name
              ? 'border-b-2 border-[var(--color-primary)] font-medium text-[var(--color-primary)]'
              : 'text-slate-600'}`}
            onClick={() => setTab(name)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'provisions' && (
        <Card>
          <p className="border-b border-slate-200 p-3 text-sm text-slate-600">
            Opening, movement and closing per person. A stored balance with no movements behind
            it cannot be explained when Finance asks — and the first time anybody looks is when
            it disagrees with the trial balance.
          </p>
          {provisions.isPending && <Skeleton className="m-4 h-24" />}
          {provisions.isError && (
            <ErrorState error={provisions.error} onRetry={() => void provisions.refetch()} />
          )}
          {provisions.isSuccess && provisions.data.length === 0 && (
            <EmptyState
              title="Nothing accrued for this month"
              description="Pick an entity and accrue it. Accruing twice replaces that month's movement rather than doubling it."
            />
          )}
          {provisions.isSuccess && provisions.data.length > 0 && (
            <div className="max-h-[32rem] overflow-auto overscroll-contain">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b border-slate-200 bg-white text-slate-600">
                  <tr>
                    <th className="p-2 text-start font-medium">Person</th>
                    <th className="p-2 text-end font-medium">Opening</th>
                    <th className="p-2 text-end font-medium">Accrued</th>
                    <th className="p-2 text-end font-medium">Used</th>
                    <th className="p-2 text-end font-medium">Closing</th>
                    <th className="p-2 text-end font-medium">Brought forward</th>
                    <th className="p-2 text-end font-medium">Accrued by HUMIS since</th>
                    <th className="p-2 text-start font-medium">Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {provisions.data.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100">
                      <td className="p-2">{row.employeeName ?? row.employeeCode}</td>
                      <td className="p-2 text-end">{row.openingBalance}</td>
                      <td className="p-2 text-end">{row.accrual}</td>
                      <td className="p-2 text-end">{row.utilisation}</td>
                      <td className="p-2 text-end font-medium">{row.closingBalance}</td>
                      <td className="p-2 text-end">
                        {row.broughtForward == null ? '—' : (
                          <>
                            {row.broughtForward}
                            <span className="block text-xs text-slate-500">
                              as of {row.broughtForwardAsOf}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="p-2 text-end">{row.accruedSince ?? '—'}</td>
                      <td className="p-2 text-xs text-slate-600">{row.basisNote ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === 'gpssa' && (
        <div className="space-y-3">
          <Card className="p-4">
            <h3 className="mb-1 flex items-center gap-2 text-base font-semibold">
              <PiggyBank className="size-4" aria-hidden="true" /> Contribution rates
            </h3>
            <p className="mb-3 text-sm text-slate-600">
              Per nationality and entity. There is no default — the rates belong to the scheme,
              not to this system, and a wrong one surfaces in a ministry reconciliation months
              later. Contributions apply to a contributory wage held between the scheme&rsquo;s
              floor and ceiling, not to gross pay.
            </p>
            {gpssaConfig.isSuccess && gpssaConfig.data.length === 0 && (
              <p className="text-sm text-amber-800">
                No rates are configured, so no contribution is calculated for anybody.
              </p>
            )}
            {gpssaConfig.data && gpssaConfig.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="p-2 text-start font-medium">Nationality</th>
                    <th className="p-2 text-start font-medium">From</th>
                    <th className="p-2 text-end font-medium">Employee</th>
                    <th className="p-2 text-end font-medium">Employer</th>
                    <th className="p-2 text-end font-medium">Government</th>
                    <th className="p-2 text-start font-medium">Wage base</th>
                  </tr>
                </thead>
                <tbody>
                  {gpssaConfig.data.map((config) => (
                    <tr key={config.id} className="border-b border-slate-100">
                      <td className="p-2">{config.nationality}</td>
                      <td className="p-2">{config.effectiveFrom}</td>
                      <td className="p-2 text-end">{config.employeeRate}%</td>
                      <td className="p-2 text-end">{config.employerRate}%</td>
                      <td className="p-2 text-end">{config.governmentRate}%</td>
                      <td className="p-2 text-slate-600">{config.wageBaseRule}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card>
            <h3 className="border-b border-slate-200 p-3 text-base font-semibold">
              Contributions for the month
            </h3>
            {gpssaReport.isSuccess && gpssaReport.data.length === 0 && (
              <p className="p-4 text-sm text-slate-600">
                Nothing was contributed in this month.
              </p>
            )}
            {gpssaReport.data && gpssaReport.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="p-2 text-start font-medium">Person</th>
                    <th className="p-2 text-start font-medium">Nationality</th>
                    <th className="p-2 text-end font-medium">Contributory wage</th>
                    <th className="p-2 text-end font-medium">Employee</th>
                    <th className="p-2 text-end font-medium">Employer</th>
                  </tr>
                </thead>
                <tbody>
                  {gpssaReport.data.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100">
                      <td className="p-2">{row.employeeName}</td>
                      <td className="p-2">{row.nationality}</td>
                      <td className="p-2 text-end">{row.contributoryWage}</td>
                      <td className="p-2 text-end">{row.employeeAmount}</td>
                      <td className="p-2 text-end">{row.employerAmount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}

      {tab === 'air-tickets' && (
        <Card>
          <h3 className="flex items-center gap-2 border-b border-slate-200 p-3 text-base font-semibold">
            <Plane className="size-4" aria-hidden="true" /> Entitlements
          </h3>
          {entitlements.isSuccess && entitlements.data.length === 0 && (
            <EmptyState
              title="No entitlement is defined"
              description="Without one, no air-ticket provision accrues for anybody."
            />
          )}
          {entitlements.data && entitlements.data.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-2 text-start font-medium">Contract</th>
                  <th className="p-2 text-start font-medium">Class</th>
                  <th className="p-2 text-end font-medium">Every</th>
                  <th className="p-2 text-end font-medium">People</th>
                  <th className="p-2 text-end font-medium">Estimated cost</th>
                  <th className="p-2 text-end font-medium">Accrues monthly</th>
                </tr>
              </thead>
              <tbody>
                {entitlements.data.map((entitlement) => (
                  <tr key={entitlement.id} className="border-b border-slate-100">
                    <td className="p-2">{entitlement.contractType ?? 'Any'}</td>
                    <td className="p-2">{entitlement.cabinClass}</td>
                    <td className="p-2 text-end">{entitlement.frequencyMonths} months</td>
                    <td className="p-2 text-end">
                      {entitlement.adultCount} adult
                      {entitlement.childCount > 0 && `, ${entitlement.childCount} child`}
                    </td>
                    <td className="p-2 text-end">
                      {entitlement.estimatedCost} {entitlement.currency}
                    </td>
                    <td className="p-2 text-end font-medium">{entitlement.monthlyAccrual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  )
}
